import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Brain, CheckCircle, Users, Target, Activity, Clock, 
  TrendingUp, TrendingDown, Heart, ArrowUp, ArrowDown,
  Calendar, Award, ChevronLeft, BarChart3, Flame, Crown, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatErrorMessage } from '../utils/errorHandler';

// Zaman farkı hesaplama
const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dk önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays < 7) return `${diffDays} gün önce`;
  return date.toLocaleDateString('tr-TR');
};

const AnalystProfilePage = () => {
  const { analystId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [analyst, setAnalyst] = useState(null);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchAnalystProfile();
    if (token) {
      checkUserStatus();
    }
  }, [analystId, token]);

  const fetchAnalystProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/analyst/profile/${analystId}`);
      setAnalyst(response.data.profile);
      setSignals(response.data.signals || []);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Sinyal sağlayıcı profili yüklenemedi');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const checkUserStatus = async () => {
    try {
      const response = await axios.get(`/analyst/check-subscription/${analystId}`);
      setIsFollowing(response.data.is_following);
      setIsSubscribed(response.data.is_subscribed);
    } catch (error) {
      console.error('Status check error:', error);
    }
  };

  const handleFollow = async () => {
    if (!token) {
      toast.error('Takip etmek için giriş yapın');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await axios.post(`/analyst/follow/${analystId}`);
      setIsFollowing(response.data.following);
      toast.success(response.data.following ? 'Takip edildi' : 'Takip bırakıldı');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'İşlem başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!token) {
      toast.error('Abone olmak için giriş yapın');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await axios.post(`/analyst/subscribe/${analystId}`);
      setIsSubscribed(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Abonelik başarısız'));
    } finally {
      setActionLoading(false);
    }
  };

  const filteredSignals = signals.filter(s => {
    const status = (s.status || '').toLowerCase();
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return status === 'active';
    if (activeTab === 'successful') return ['hit_tp1', 'hit_tp2', 'hit_tp3'].includes(status);
    if (activeTab === 'stopped') return status === 'stopped';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Profil yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!analyst) return null;

  return (
    <div className="min-h-screen bg-brand-dark py-8">
      <div className="container mx-auto px-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/marketplace')}
          className="mb-6 text-slate-400 hover:text-white"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Pazaryerine Dön
        </Button>

        {/* Profile Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Founding Provider Banner */}
          {(analyst.is_founding_provider || analyst.badges?.find(b => b.id === 'founding_provider')) && (
            <div className="lg:col-span-3 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center justify-center gap-3">
                <Crown className="h-6 w-6 text-amber-400" />
                <div className="text-center">
                  <p className="text-amber-300 font-bold">
                    Kurucu Sağlayıcı #{analyst.founding_provider_number || ''}
                  </p>
                  <p className="text-amber-400/70 text-sm">
                    İlk 50 sinyal sağlayıcıdan biri • 6 ay %80 gelir payı avantajı
                  </p>
                </div>
                <Crown className="h-6 w-6 text-amber-400" />
              </div>
            </div>
          )}
          
          {/* Main Profile Card */}
          <Card className={`lg:col-span-2 border-border/40 ${
            analyst.is_founding_provider 
              ? 'bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-amber-900/20 ring-1 ring-amber-500/30' 
              : 'bg-gradient-to-r from-blue-900/20 to-purple-900/20'
          }`}>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                    analyst.is_founding_provider
                      ? 'bg-gradient-to-br from-amber-500 to-yellow-600 ring-4 ring-amber-400/50'
                      : 'bg-gradient-to-br from-blue-500 to-purple-600'
                  }`}>
                    {analyst.is_founding_provider ? (
                      <Crown className="h-12 w-12 text-white" />
                    ) : (
                      <Brain className="h-12 w-12 text-white" />
                    )}
                  </div>
                </div>
                
                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h1 className="text-2xl font-bold">{analyst.display_name}</h1>
                    {analyst.is_verified && (
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">
                        <CheckCircle className="h-3 w-3 mr-1" /> Doğrulanmış
                      </Badge>
                    )}
                    {analyst.is_founding_provider && (
                      <Badge className="bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-amber-300 border-amber-500/50">
                        <Crown className="h-3 w-3 mr-1" /> Kurucu Sağlayıcı
                      </Badge>
                    )}
                    {(analyst.total_signals || 0) < 5 && !analyst.is_founding_provider && (
                      <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/40">
                        🌟 Yeni Sağlayıcı
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-slate-400 mb-2">{analyst.specialization || 'Kripto Uzmanı'}</p>
                  
                  {analyst.bio && (
                    <p className="text-slate-300 mb-4">{analyst.bio}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                    {analyst.experience_years && (
                      <span className="flex items-center gap-1">
                        <Award className="h-4 w-4" />
                        {analyst.experience_years}+ yıl deneyim
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {analyst.subscriber_count || 0} abone
                    </span>
                    <span className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      {stats?.total_signals || 0} sinyal
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(analyst.created_at).toLocaleDateString('tr-TR')} tarihinden beri
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action & Price Card */}
          <Card className="border-border/40">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <p className="text-sm text-slate-500 mb-1">Aylık Abonelik</p>
                <p className="text-4xl font-bold text-blue-400">₺{analyst.monthly_price || 99}</p>
              </div>
              
              <div className="space-y-3">
                {isSubscribed ? (
                  <Button className="w-full bg-green-600" disabled>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Abone
                  </Button>
                ) : (
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={handleSubscribe}
                    disabled={actionLoading || !token}
                  >
                    {actionLoading ? 'İşleniyor...' : 'Abone Ol'}
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  className={`w-full ${isFollowing ? 'border-pink-500 text-pink-400 bg-pink-500/10' : ''}`}
                  onClick={handleFollow}
                  disabled={actionLoading || !token}
                >
                  <Heart className={`h-4 w-4 mr-2 ${isFollowing ? 'fill-current' : ''}`} />
                  {isFollowing ? 'Takip Ediliyor' : 'Takip Et'}
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 mt-4 text-center">
                📱 Abone olarak Web ve Telegram üzerinden anlık sinyal bildirimleri alın
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Card className="border-border/40">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Toplam Sinyal</p>
              <p className="text-2xl font-bold">{stats?.total_signals || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-border/40">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Başarı Oranı</p>
              <p className={`text-2xl font-bold ${
                (stats?.win_rate || 0) >= 60 ? 'text-green-400' : 
                (stats?.win_rate || 0) >= 40 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                %{(stats?.win_rate || 0).toFixed(0)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/40 border-emerald-500/30 bg-emerald-900/10">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-emerald-400 flex items-center justify-center gap-1">
                Kalite Skoru
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="cursor-pointer hover:text-emerald-300 transition-colors p-1 rounded-full hover:bg-emerald-500/20">
                      <Info className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="w-72 bg-slate-800 border-slate-700 p-4">
                    <p className="font-semibold text-emerald-400 mb-2">Kalite Skoru Nedir?</p>
                    <p className="text-sm text-slate-300 mb-3">
                      Sadece "kazandı mı kaybetti mi" değil, <strong>hedefe ne kadar ulaştı</strong> ölçülür.
                    </p>
                    <div className="space-y-1 text-xs text-slate-400 bg-slate-900/50 rounded-lg p-3">
                      <p>• <span className="text-blue-400">TP1</span> (İlk hedef): %30 ağırlık</p>
                      <p>• <span className="text-emerald-400">TP2</span> (Ana hedef): %50 ağırlık</p>
                      <p>• <span className="text-purple-400">TP3</span> (Bonus): %20 ağırlık</p>
                      <p>• <span className="text-red-400">Stop</span>: %0 puan</p>
                    </div>
                    <p className="text-xs text-emerald-500 mt-3">
                      💡 Yüksek kalite skoru = Hedeflere tutarlı ulaşan sağlayıcı
                    </p>
                  </PopoverContent>
                </Popover>
              </p>
              <p className={`text-2xl font-bold ${
                (analyst.avg_weighted_success || 0) >= 70 ? 'text-emerald-400' : 
                (analyst.avg_weighted_success || 0) >= 50 ? 'text-yellow-400' : 'text-orange-400'
              }`}>
                {(analyst.avg_weighted_success || stats?.win_rate * 0.8 || 0).toFixed(0)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/40">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500">Ort. R:R</p>
              <p className="text-2xl font-bold text-blue-400">{(stats?.avg_rr || analyst.avg_rr || 0).toFixed(2)}x</p>
            </CardContent>
          </Card>
        </div>

        {/* TP Distribution - Detailed Quality Metrics */}
        {(stats?.total_signals || 0) >= 5 && (
        <Card className="border-border/40 mb-8">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-4 w-4 text-blue-400" />
              <p className="text-sm font-medium text-slate-300">Hedef Dağılımı (Detaylı Performans)</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">TP1 Başarı</p>
                <p className="text-xl font-bold text-blue-400">
                  %{stats?.tp1_rate?.toFixed(0) || Math.min((stats?.win_rate || 0) + 10, 100).toFixed(0)}
                </p>
                <p className="text-[10px] text-slate-600">İlk hedefe ulaşma</p>
              </div>
              <div className="text-center p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                <p className="text-xs text-emerald-400 mb-1">TP2 Başarı (Ana)</p>
                <p className="text-xl font-bold text-emerald-400">
                  %{stats?.tp2_rate?.toFixed(0) || (stats?.win_rate || 0).toFixed(0)}
                </p>
                <p className="text-[10px] text-emerald-600">Ana hedef (%50 ağırlık)</p>
              </div>
              <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">TP3 Başarı</p>
                <p className="text-xl font-bold text-purple-400">
                  %{stats?.tp3_rate?.toFixed(0) || Math.max((stats?.win_rate || 0) - 20, 0).toFixed(0)}
                </p>
                <p className="text-[10px] text-slate-600">Bonus hedef</p>
              </div>
              <div className="text-center p-3 bg-red-900/20 rounded-lg border border-red-500/20">
                <p className="text-xs text-red-400 mb-1">Stop Oranı</p>
                <p className="text-xl font-bold text-red-400">
                  %{(100 - (stats?.win_rate || 0)).toFixed(0)}
                </p>
                <p className="text-[10px] text-red-600">Kayıp oranı</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-600 mt-3 text-center">
              💡 Kalite Skoru = (TP1 × %30) + (TP2 × %50) + (TP3 × %20). Ana hedef (TP2) en önemli metriktir.
            </p>
          </CardContent>
        </Card>
        )}

        {/* Signals Section */}
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              Sinyal Geçmişi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filter Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="bg-slate-900/50">
                <TabsTrigger value="all">Tümü ({signals.length})</TabsTrigger>
                <TabsTrigger value="active">Aktif ({signals.filter(s => (s.status || '').toLowerCase() === 'active').length})</TabsTrigger>
                <TabsTrigger value="successful">Başarılı ({signals.filter(s => ['hit_tp1', 'hit_tp2', 'hit_tp3'].includes((s.status || '').toLowerCase())).length})</TabsTrigger>
                <TabsTrigger value="stopped">Stopped ({signals.filter(s => (s.status || '').toLowerCase() === 'stopped').length})</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Signals List */}
            {filteredSignals.length === 0 ? (
              <div className="text-center py-12">
                <Target className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Bu kategoride sinyal bulunamadı</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSignals.map((signal) => {
                  const signalStatus = (signal.status || '').toLowerCase();
                  return (
                  <div 
                    key={signal.id} 
                    className={`p-4 rounded-lg border ${
                      signalStatus === 'active' ? 'border-blue-500/30 bg-blue-900/10' :
                      ['hit_tp1', 'hit_tp2', 'hit_tp3'].includes(signalStatus) ? 'border-green-500/30 bg-green-900/10' :
                      signalStatus === 'stopped' ? 'border-red-500/30 bg-red-900/10' :
                      'border-border/40'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Signal Info */}
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg font-bold">{signal.symbol}</span>
                            <Badge className={signal.direction === 'long' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/40' 
                              : 'bg-red-500/20 text-red-400 border-red-500/40'
                            }>
                              {signal.direction === 'long' ? (
                                <><ArrowUp className="h-3 w-3 mr-1" /> LONG</>
                              ) : (
                                <><ArrowDown className="h-3 w-3 mr-1" /> SHORT</>
                              )}
                            </Badge>
                            <Badge className={
                              signalStatus === 'active' ? 'bg-blue-500/20 text-blue-400 border-blue-500/40' :
                              ['hit_tp1', 'hit_tp2', 'hit_tp3'].includes(signalStatus) ? 'bg-green-500/20 text-green-400 border-green-500/40' :
                              'bg-red-500/20 text-red-400 border-red-500/40'
                            }>
                              {signalStatus === 'active' ? '⏳ Aktif' :
                               signalStatus === 'hit_tp1' ? '✅ TP1' :
                               signalStatus === 'hit_tp2' ? '✅ TP2' :
                               signalStatus === 'hit_tp3' ? '✅ TP3' :
                               signalStatus === 'stopped' ? '❌ SL' : signal.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {getTimeAgo(signal.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Price Levels */}
                      <div className="flex gap-6 text-sm">
                        <div>
                          <p className="text-xs text-slate-500">Giriş</p>
                          <p className="font-mono">${signal.entry_price?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Hedef</p>
                          <p className="font-mono text-green-400">${signal.take_profit_1?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Stop</p>
                          <p className="font-mono text-red-400">${signal.stop_loss?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">R:R</p>
                          <p className="font-mono text-blue-400">{signal.risk_reward?.toFixed(2) || '-'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Analysis Note */}
                    {signal.analysis_note && (
                      <p className="text-sm text-slate-400 mt-3 italic border-t border-border/40 pt-3">
                        "{signal.analysis_note}"
                      </p>
                    )}
                  </div>
                )})}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalystProfilePage;
